import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  AdvancedSettings,
  AutomationCard,
  EditButton,
  SettingGroup,
  SettingToggle,
  SettingsCard,
  ToggleAllButton,
  ValueTiles,
} from '@/components/settings/SettingsParts'
import {
  SO_FLAG_FIELDS,
  type AutomationSettings,
  type ErpSettings,
  type OrderSourceSettings,
  type ParseFormatSettings,
  type PriceThresholds,
  type QuantitySettings,
  type SoFlagFieldId,
  type StockSettings,
} from '@/features/sales-order/settings/salesOrderSettingsMappers'

/**
 * The sections both settings pages are built from — the workflow's and a customer's. Both pages
 * show every section, because a customer's settings are the workflow's settings with different
 * values in them, and a setting that were shown on only one of the two would be changed on one
 * page and silently overridden on the other.
 */

export function AutomationSection({
  automation,
  onToggle,
}: {
  automation: AutomationSettings
  onToggle: (checked: boolean) => void
}) {
  return (
    <AutomationCard
      checked={automation.fullyAutomaticValidation}
      onChange={onToggle}
      description="Creates the sales order without a person looking at it, when the order passes every check below."
      whenOn="On: orders that pass every check are created automatically"
      whenOff="Off: every order waits for your team"
    />
  )
}

function validateThresholds(
  orderInput: string,
  rowInput: string,
): { ok: true; value: PriceThresholds } | { ok: false; error: string } {
  const totalOrderPrice = Number(orderInput)
  if (!Number.isFinite(totalOrderPrice) || totalOrderPrice < 0) {
    return { ok: false, error: 'Order total must be 0 or higher.' }
  }

  const totalRowPrice = Number(rowInput)
  if (!Number.isFinite(totalRowPrice) || totalRowPrice < 0) {
    return { ok: false, error: 'Row total must be 0 or higher.' }
  }

  return { ok: true, value: { totalOrderPrice, totalRowPrice } }
}

export function PriceThresholdsSection({
  thresholds,
  onDraftChange,
}: {
  thresholds: PriceThresholds
  onDraftChange: (next: PriceThresholds) => void
}) {
  const [editing, setEditing] = useState(false)
  const [orderInput, setOrderInput] = useState(String(thresholds.totalOrderPrice))
  const [rowInput, setRowInput] = useState(String(thresholds.totalRowPrice))
  const [error, setError] = useState<string | null>(null)

  function startEdit() {
    setOrderInput(String(thresholds.totalOrderPrice))
    setRowInput(String(thresholds.totalRowPrice))
    setError(null)
    setEditing(true)
  }

  function applyEdit() {
    const result = validateThresholds(orderInput, rowInput)
    if (!result.ok) {
      setError(result.error)
      return
    }
    onDraftChange(result.value)
    setError(null)
    setEditing(false)
  }

  return (
    <SettingsCard
      title="Price thresholds"
      about="An order or a row worth more than this is put in front of a person even when fully automatic validation is on. 0 stops every order."
      description="Orders at these totals or more wait for your team"
      actions={!editing && <EditButton label="Edit price thresholds" onClick={startEdit} />}
    >
      {editing ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-700">Order total</span>
              <Input type="number" min={0} step={100} inputMode="decimal" value={orderInput} onChange={(e) => setOrderInput(e.target.value)} />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-700">Row total</span>
              <Input type="number" min={0} step={100} inputMode="decimal" value={rowInput} onChange={(e) => setRowInput(e.target.value)} />
            </label>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={applyEdit}>Apply</Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <ValueTiles
          items={[
            { label: 'Order total', value: thresholds.totalOrderPrice.toLocaleString('sv-SE') },
            { label: 'Row total', value: thresholds.totalRowPrice.toLocaleString('sv-SE') },
          ]}
        />
      )}
    </SettingsCard>
  )
}

export function FlagDifferencesSection({
  flaggedFields,
  onToggle,
  onToggleAll,
}: {
  flaggedFields: Set<SoFlagFieldId>
  onToggle: (id: SoFlagFieldId, checked: boolean) => void
  onToggleAll: (on: boolean) => void
}) {
  const allOn = flaggedFields.size === SO_FLAG_FIELDS.length
  return (
    <SettingsCard
      title="Flag differences in"
      about="Which differences between the customer's order and what we matched it to should stop the order for review."
      description={`${flaggedFields.size} of ${SO_FLAG_FIELDS.length} fields are flagged`}
      actions={
        <ToggleAllButton
          allOn={allOn}
          onToggle={onToggleAll}
          what="flagged fields"
        />
      }
    >
      <div className="grid gap-x-6 sm:grid-cols-2">
        {SO_FLAG_FIELDS.map((field) => (
          <SettingToggle
            key={field.id}
            layout="compact"
            label={field.label}
            checked={flaggedFields.has(field.id)}
            onChange={(checked) => onToggle(field.id, checked)}
          />
        ))}
      </div>
    </SettingsCard>
  )
}

export function StockSection({
  stock,
  onToggle,
}: {
  stock: StockSettings
  onToggle: (checked: boolean) => void
}) {
  return (
    <SettingsCard title="Stock">
      <SettingToggle
        label="Check orders against stock"
        description="Reads the stock balance for every article on an order and shows it while the order is validated."
        checked={stock.checkStockBalance}
        onChange={onToggle}
      />
    </SettingsCard>
  )
}

export function ErpSection({
  erp,
  onToggle,
}: {
  erp: ErpSettings
  onToggle: (checked: boolean) => void
}) {
  return (
    <SettingsCard title="ERP">
      <SettingToggle
        label="Upload the customer's order document to the ERP"
        description="Files the original document with the sales order it became."
        checked={erp.uploadOrderDocumentToErp}
        onChange={onToggle}
      />
    </SettingsCard>
  )
}

export function OrderSourceSection({
  source,
  onChange,
}: {
  source: OrderSourceSettings
  onChange: (next: OrderSourceSettings) => void
}) {
  return (
    <SettingsCard
      title="Delivery address and contact person"
      about="Where these are taken from when the sales order is created. By default we send what we matched against your ERP's own records. Use the customer's document instead where their orders state a delivery address or contact person your ERP does not hold."
      description="Off: taken from your ERP's records"
    >
      <div className="divide-y divide-gray-100">
        <SettingToggle
          label="Use the delivery address from the customer's order"
          checked={source.useExtractedDeliveryAddress}
          onChange={(checked) => onChange({ ...source, useExtractedDeliveryAddress: checked })}
        />
        <SettingToggle
          label="Use the contact person from the customer's order"
          checked={source.useExtractedCustomerContactPerson}
          onChange={(checked) => onChange({ ...source, useExtractedCustomerContactPerson: checked })}
        />
      </div>
    </SettingsCard>
  )
}

export function AdvancedSettingsSection({ children }: { children: React.ReactNode }) {
  return <AdvancedSettings>{children}</AdvancedSettings>
}

export function ParseFormatSection({
  parseFormat,
  onToggle,
}: {
  parseFormat: ParseFormatSettings
  onToggle: (key: keyof ParseFormatSettings, checked: boolean) => void
  nested?: boolean
}) {
  const noDateFormatSelected =
    !parseFormat.dateParsingWeekFormat &&
    !parseFormat.dateParsingAmericanFormat &&
    !parseFormat.dateParsingIsoFormat &&
    !parseFormat.dateParsingDayFirstFormat

  return (
    <SettingsCard
      title="Parsing format"
      description="Which formats a customer's document is read with. At least one date format stays on."
    >
      <SettingGroup title="Numbers">
        <SettingToggle
          layout="compact"
          label="Choose the number format rather than reading it as written"
          checked={parseFormat.useFloatParsingSettings}
          onChange={(checked) => onToggle('useFloatParsingSettings', checked)}
        />
        {/* Only honoured when the setting above is on; the parser ignores it otherwise. */}
        <div className="pl-12">
          <SettingToggle
            layout="compact"
            label="English number format (1,234.56)"
            checked={parseFormat.floatParsingEnglishFormat}
            onChange={(checked) => onToggle('floatParsingEnglishFormat', checked)}
            disabled={!parseFormat.useFloatParsingSettings}
          />
        </div>
      </SettingGroup>
      <SettingGroup title="Dates" className="mt-3">
        <SettingToggle layout="compact" label="Week number (YYWWD)" checked={parseFormat.dateParsingWeekFormat} onChange={(checked) => onToggle('dateParsingWeekFormat', checked)} />
        <SettingToggle layout="compact" label="American (MM/DD/YYYY)" checked={parseFormat.dateParsingAmericanFormat} onChange={(checked) => onToggle('dateParsingAmericanFormat', checked)} />
        <SettingToggle layout="compact" label="ISO 8601 (YYYY-MM-DD)" checked={parseFormat.dateParsingIsoFormat} onChange={(checked) => onToggle('dateParsingIsoFormat', checked)} />
        <SettingToggle layout="compact" label="Day first (DD-MM-YYYY)" checked={parseFormat.dateParsingDayFirstFormat} onChange={(checked) => onToggle('dateParsingDayFirstFormat', checked)} />
      </SettingGroup>
      {noDateFormatSelected && (
        <p className="mt-3 text-sm text-red-600">At least one date format must be on.</p>
      )}
    </SettingsCard>
  )
}

export function QuantitySection({
  quantity,
  onToggle,
}: {
  quantity: QuantitySettings
  onToggle: (checked: boolean) => void
  nested?: boolean
}) {
  return (
    <SettingsCard title="Quantity">
      <SettingToggle
        label="Read a quantity in packages as the number of units it holds"
        description="For documents that order in packages and state how many units a package holds."
        checked={quantity.multiplyQuantityByUnitsPerPackage}
        onChange={onToggle}
      />
    </SettingsCard>
  )
}
